import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Icon from '../../components/AppIcon';
import ContactForm from './components/ContactForm';
import ContactInfo from './components/ContactInfo';
import Footer from "../home/components/Footer";

const heroVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.6, 
      ease: "easeOut",
      staggerChildren: 0.2 
    } 
  },
};

const childVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    y: 0, 
    transition: { 
      duration: 0.4, 
      ease: "easeOut"
    } 
  },
};

const sectionVariants = {
  hidden: { opacity: 0 },
  visible: { 
    opacity: 1, 
    transition: { 
      duration: 0.5, 
      delay: 0.5, 
      staggerChildren: 0.3
    } 
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -50 },
  visible: { 
    opacity: 1, 
    x: 0, 
    transition: { 
      type: "spring", 
      stiffness: 100,
      damping: 20
    } 
  },
};

const Contact = () => {
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleFormSubmit = () => {
    setFormSubmitted(true);
    setTimeout(() => setFormSubmitted(false), 5000);
  };

  return (
    <>
      <Helmet>
        <title>Contact Us - Zentirx Infotech | Get in Touch</title>
        <meta name="description" content="Contact Zentirx Infotech for your IT service needs. Reach out through our contact form, phone, or visit our offices. We're here to help with your technology solutions." />
        <meta name="keywords" content="contact zentirx, IT services contact, technology consultation, software development inquiry" />
      </Helmet>

      <motion.div 
        className="min-h-screen bg-background pt-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        
        <motion.section 
          className="relative overflow-hidden bg-gradient-to-br from-primary-50 to-accent-50 py-16 lg:py-24"
          initial="hidden"
          animate="visible"
          variants={heroVariants}
        >
          <div className="absolute inset-0 opacity-20 [mask-image:radial-gradient(ellipse_at_center,white,transparent)]">
             <motion.div 
                className="absolute -top-10 -left-10 w-40 h-40 bg-primary rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse"
                animate={{ scale: [1, 1.2, 1], rotate: [0, 90, 0] }}
                transition={{ duration: 10, repeat: Infinity, ease: "linear" }}
             />
             <motion.div 
                className="absolute bottom-0 right-10 w-60 h-60 bg-accent rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse delay-1000"
                animate={{ scale: [1, 0.8, 1], rotate: [0, -90, 0] }}
                transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
             />
          </div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center max-w-3xl mx-auto">
              <motion.h1 
                className="text-4xl lg:text-5xl font-heading font-extrabold text-text-primary mb-6"
                variants={childVariants}
              >
                Get in Touch 
              </motion.h1>
              <motion.p 
                className="text-xl text-text-secondary mb-8"
                variants={childVariants}
              >
                Ready to transform your business with cutting-edge technology? Let's discuss your project and explore how we can help you achieve your goals.
              </motion.p>
              <motion.div 
                className="flex flex-wrap justify-center gap-4"
                variants={childVariants}
              >
                <motion.a
                  href="tel:+919925627972"
                  className="inline-flex items-center px-8 py-3 bg-primary text-white font-semibold rounded-full hover:bg-primary-700 transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 focus-ring"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Icon name="Phone" size={20} className="mr-2" />
                  Call Now
                </motion.a>
                <motion.a
                  href="mailto:info@zentirx.com"
                  className="inline-flex items-center px-8 py-3 bg-surface text-primary border-2 border-primary font-semibold rounded-full hover:bg-primary hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl hover:scale-105 focus-ring"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Icon name="Mail" size={20} className="mr-2" />
                  Email Us
                </motion.a>
              </motion.div>
            </div>
          </div>
        </motion.section>

        <motion.section 
          className="py-16 lg:py-10"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={sectionVariants}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              
              <motion.div 
                className="lg:col-span-7"
                variants={itemVariants}
              >
                <div className="bg-surface rounded-2xl shadow-xl p-8 lg:p-12 border border-gray-100">
                  <div className="mb-8">
                    <h2 className="text-3xl font-heading font-bold text-text-primary mb-2">
                      Send us a Message 
                    </h2>
                    <p className="text-text-secondary">
                      Fill out the form below and we'll get back to you within 24 hours. All fields marked with * are required.
                    </p>
                  </div>

                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={formSubmitted ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
                    transition={{ duration: 0.5 }}
                    className={`mb-6 p-4 bg-success-50 border border-success-100 rounded-lg ${formSubmitted ? 'block' : 'hidden'}`}
                  >
                    <div className="flex items-center text-success-700">
                      <Icon name="CheckCircle" size={20} className="mr-3 flex-shrink-0" />
                      <p className="font-medium">
                        Thank you! Your message has been sent successfully. We'll get back to you soon.
                      </p>
                    </div>
                  </motion.div>
                  
                  <ContactForm onSubmit={handleFormSubmit} />
                </div>
              </motion.div>

              <motion.div 
                className="lg:col-span-5 space-y-8"
                variants={{ 
                  hidden: { opacity: 0, x: 50 },
                  visible: { 
                    opacity: 1, 
                    x: 0, 
                    transition: { 
                      type: "spring", 
                      stiffness: 100,
                      damping: 20
                    } 
                  },
                }}
              >
                <ContactInfo />
            
              </motion.div>
            </div>
          </div>
        </motion.section>

        <section className="py-16 lg:py-2 bg-secondary-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-heading font-bold text-text-primary mb-4">
                Other Ways to Connect 
              </h2>
              <p className="text-text-secondary max-w-2xl mx-auto">
                Choose the communication method that works best for you. We're available across multiple channels to ensure you can reach us easily.
              </p>
            </div>

            <motion.div 
              className="flex justify-center flex-wrap gap-8"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={{
                hidden: { opacity: 0 },
                visible: {
                  opacity: 1,
                  transition: {
                    staggerChildren: 0.15
                  }
                }
              }}
            >
              
              <motion.div 
                className="bg-surface rounded-xl p-8 text-center shadow-lg hover:shadow-2xl transition-shadow duration-300 w-full max-w-sm md:w-72 border border-gray-100"
                whileHover={{ scale: 1.05, y: -5 }}
                variants={childVariants}
              >
                <div className="w-14 h-14 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="MessageCircle" size={28} className="text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-text-primary mb-2">Live Chat</h3>
                <p className="text-base text-text-secondary mb-4">
                  Chat with our support team in real-time
                </p>
                <button className="text-primary hover:text-primary-700 font-medium text-base hover:underline">
                  Start Chat &rarr;
                </button>
              </motion.div>

              <motion.div 
                className="bg-surface rounded-xl p-8 text-center shadow-lg hover:shadow-2xl transition-shadow duration-300 w-full max-w-sm md:w-72 border border-gray-100"
                whileHover={{ scale: 1.05, y: -5 }}
                variants={childVariants}
              >
                <div className="w-14 h-14 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="MessageSquare" size={28} className="text-success" />
                </div>
                <h3 className="text-xl font-semibold text-text-primary mb-2">WhatsApp</h3>
                <p className="text-base text-text-secondary mb-4">
                  Message us on WhatsApp for quick responses
                </p>
                <a
                  href="https://wa.me/+919925627972"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-success hover:text-success-600 font-medium text-base hover:underline"
                >
                  Send Message &rarr;
                </a>
              </motion.div>


              <motion.div 
                className="bg-surface rounded-xl p-8 text-center shadow-lg hover:shadow-2xl transition-shadow duration-300 w-full max-w-sm md:w-72 border border-gray-100"
                whileHover={{ scale: 1.05, y: -5 }}
                variants={childVariants}
              >
                <div className="w-14 h-14 bg-accent-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Icon name="Calendar" size={28} className="text-accent" />
                </div>
                <h3 className="text-xl font-semibold text-text-primary mb-2">Schedule Call</h3>
                <p className="text-base text-text-secondary mb-4">
                  Book a consultation call at your convenience
                </p>
                <button className="text-accent hover:text-accent-600 font-medium text-base hover:underline">
                  Book Now &rarr;
                </button>
              </motion.div>

            </motion.div>
          </div>
        </section>

        <section className="py-16 lg:py-10">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-heading font-bold text-text-primary mb-2">
                Frequently Asked Questions 
              </h2>
              <p className="text-text-secondary">
                Quick answers to common questions about our services and processes.
              </p>
            </div>

            <motion.div 
              className="space-y-4"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={{
                hidden: { opacity: 0 },
                visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
              }}
            >
              {[
                {
                  question: "What is your typical project timeline?",
                  answer: "Project timelines vary based on complexity and scope. Simple websites typically take 2-4 weeks, while complex applications can take 3-6 months. We provide detailed timelines during our initial consultation."
                },
                {
                  question: "Do you provide ongoing support and maintenance?",
                  answer: "Yes, we offer comprehensive support and maintenance packages to ensure your applications remain secure, updated, and performing optimally. Our support includes bug fixes, security updates, and feature enhancements."
                },
                {
                  question: "What technologies do you specialize in?",
                  answer: "We specialize in modern web technologies including React, Node.js, Python, cloud platforms (AWS, Azure), mobile development (React Native, Flutter), and database technologies (PostgreSQL, MongoDB)."
                },
                {
                  question: "How do you handle project communication?",
                  answer: "We maintain transparent communication through regular updates, weekly progress reports, and dedicated project management tools. You'll have direct access to your project team and regular milestone reviews."
                }
              ].map((faq, index) => (
                <FAQItem key={index} faq={faq} /> 
              ))}
            </motion.div>
          </div>
        </section>
        
        <Footer />
      </motion.div>
    </>
  );
};

const FAQItem = ({ faq }) => {
    const [isOpen, setIsOpen] = useState(false);
    
    const answerVariants = {
        open: { opacity: 1, height: "auto" },
        closed: { opacity: 0, height: 0 }
    };

    return (
        <motion.div 
            className="bg-surface rounded-xl p-6 shadow-md border border-gray-100 overflow-hidden cursor-pointer"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            onClick={() => setIsOpen(!isOpen)}
        >
            <h3 className="font-semibold text-text-primary mb-3 flex items-center justify-between">
                <span className="flex items-center">
                    <Icon name="HelpCircle" size={20} className="text-primary mr-3 flex-shrink-0" />
                    {faq.question}
                </span>
                <motion.span
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                >
                    <Icon name="ChevronUp" size={24} className="text-primary" />
                </motion.span>
            </h3>
            
            <motion.div
                initial="closed"
                animate={isOpen ? "open" : "closed"}
                variants={answerVariants}
                transition={{ duration: 0.5, ease: "easeInOut" }}
            >
                <p className="text-text-secondary pt-4 border-t border-gray-100 mt-4">
                    {faq.answer}
                </p>
            </motion.div>
        </motion.div>
    );
};


export default Contact;